import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectOptions = [
  "Full home renovation",
  "Kitchen remodel",
  "Bathroom remodel",
  "Addition / Extension",
  "Brownstone / Co-op",
  "New build",
  "Just exploring",
];

export default function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", location: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  useEffect(() => { if (!open) { setSuccess(false); setError(""); } }, [open]);

  if (!open) return null;

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", project_type: "", location: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[var(--slate-950)]/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[var(--off-white)] w-full max-w-2xl my-8 relative shadow-2xl border border-[var(--slate-900)]" onClick={(e) => e.stopPropagation()}>
        {/* corner brackets */}
        <span className="absolute -top-px -left-px w-4 h-4 border-t-2 border-l-2 border-[var(--terracotta)]" />
        <span className="absolute -bottom-px -right-px w-4 h-4 border-b-2 border-r-2 border-[var(--terracotta)]" />

        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-[var(--slate-900)] hover:bg-[var(--bg-secondary)] transition-colors z-10">
          <X size={20} />
        </button>

        {success ? (
          <div className="p-10 md:p-14 text-center">
            <CheckCircle2 size={56} className="mx-auto text-[var(--terracotta)] mb-4" strokeWidth={1.4} />
            <p className="section-tag mb-3">// SUBMISSION RECEIVED</p>
            <h3 className="font-display text-[42px] uppercase leading-tight text-[var(--slate-900)]">Thanks for reaching out</h3>
            <p className="mt-4 text-[14px] text-[var(--slate-700)] max-w-md mx-auto leading-7">
              Your inquiry has been routed to <span className="font-semibold">info@edge-developerllc.com</span>. A member of the Edge Developer team will be in touch within one business day.
            </p>
            <button onClick={onClose} className="btn-solid mt-8">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8 md:p-10">
            <p className="section-tag mb-3">// PROJECT INQUIRY</p>
            <h3 className="font-display text-[34px] sm:text-[44px] uppercase leading-[0.95] text-[var(--slate-900)]">
              Start your project<br /><span className="text-[var(--terracotta)]">with Edge Developer</span>
            </h3>
            <p className="mt-3 text-[13px] text-[var(--slate-700)] max-w-lg">Tell us a bit about your renovation. Our team replies within one business day.</p>

            <div className="grid sm:grid-cols-2 gap-4 mt-7">
              <Field label="Full Name" name="name" value={form.name} onChange={handleChange} required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
              <Field label="Phone (optional)" name="phone" value={form.phone} onChange={handleChange} />
              <Field label="Project Location" name="location" value={form.location} onChange={handleChange} placeholder="e.g. Manhattan, NY" />
            </div>

            <div className="mt-4">
              <label className="block font-mono-sans text-[10.5px] font-semibold text-[var(--slate-900)] mb-1.5">PROJECT TYPE</label>
              <select name="project_type" value={form.project_type} onChange={handleChange}
                className="w-full bg-transparent border border-[var(--slate-300)] px-3 py-2.5 text-[13.5px] text-[var(--slate-900)] focus:outline-none focus:border-[var(--terracotta)] transition-colors">
                <option value="">Select...</option>
                {projectOptions.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>

            <div className="mt-4">
              <label className="block font-mono-sans text-[10.5px] font-semibold text-[var(--slate-900)] mb-1.5">TELL US ABOUT YOUR PROJECT</label>
              <textarea name="message" value={form.message} onChange={handleChange} required rows={4} placeholder="Scope, timeline, budget range, anything we should know..."
                className="w-full bg-transparent border border-[var(--slate-300)] px-3 py-2.5 text-[13.5px] text-[var(--slate-900)] focus:outline-none focus:border-[var(--terracotta)] transition-colors resize-y" />
            </div>

            {error && (
              <div className="mt-4 text-[13px] text-[var(--terracotta-dark)] bg-[#f6e1de] border border-[var(--terracotta-light)] px-3 py-2">{error}</div>
            )}

            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <p className="font-mono-sans text-[10.5px] text-[var(--slate-500)] tracking-wider">DELIVERED TO INFO@EDGE-DEVELOPERLLC.COM</p>
              <button type="submit" disabled={submitting} className="btn-terracotta disabled:opacity-60 disabled:cursor-not-allowed">
                {submitting ? "Sending..." : (<>Send Inquiry <Send size={14} /></>)}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block font-mono-sans text-[10.5px] font-semibold text-[var(--slate-900)] mb-1.5">
        {label.toUpperCase()}{required && <span className="text-[var(--terracotta)]"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full bg-transparent border border-[var(--slate-300)] px-3 py-2.5 text-[13.5px] text-[var(--slate-900)] focus:outline-none focus:border-[var(--terracotta)] transition-colors"
      />
    </div>
  );
}
